/**
 * grantOrder — which components an edge renders, and in what order (F2).
 *
 * ORDER IS NORMATIVE. A component's position is part of its definition, not a
 * layout preference. Every page is a SUBSEQUENCE of the canonical order below;
 * an edge may omit a component but may never reorder one.
 *
 *   1 explanation → 2 the ask → 3 the realm → 4 the grant
 *     → 5 the boundary → 6 the terms → 7 decide
 *
 * WHY THE ASK PRECEDES THE REALM. The ask is information; the realm is a
 * decision. Ranking realms by "covers 3 of 4" is noise until the reader knows
 * what the four are. It also separates what THEY want (C3, read-only) from what
 * YOU give (C5, actionable) — the DLR/EP distinction the old page collapsed into
 * one surface, which is what made a separate "Restrict Further" panel necessary.
 *
 * WHY C5P IS GONE TOO. It let you pick which of the realm's types to share —
 * which is a second selection over the same nouns the realm already selected.
 * Two lists of schemas with checkboxes, one above the other, presented as equal
 * decisions, when the first one already answered the question. A realm is a
 * named, reusable statement of what may be shared; asking again immediately
 * afterwards says it wasn't.
 *
 * So a no-DLR grant is exactly its realms. Narrowing below one means picking a
 * narrower realm — or making one, which is what realms are for.
 *
 * WHY C5 IS GONE. It was "the ask, made actionable after a realm is chosen" —
 * which meant rendering the same list twice with a realm picker wedged between,
 * and refusing to let anyone touch the first copy. Two problems, both real:
 *
 *   The request is what the person came to judge. Burying it under a decision
 *   they cannot yet make — pick a ceiling, before seeing what it is for — gets
 *   the order backwards, and a summary in its place is not the request.
 *
 *   Ticking is an expression of intent. It does not need a ceiling to be
 *   meaningful, and a page that refuses the click reads as broken rather than
 *   careful.
 *
 * So there is ONE ask, at step 2, editable from the start. The realm still
 * decides what any of it YIELDS: a ticked row the realm cannot reach is marked
 * unavailable and grants nothing. Intent above, ceiling below, and the boundary
 * shows the intersection.
 */

/** The canonical order. Index here IS the sort key; never reorder this array. */
export const CANONICAL = [
  'C1',   // explanation — who is asking
  'C2',   // acting-as — whose data is at stake, when it isn't yours
  'WHY',  // what happened to THIS grant before — "Last granted 19 Sept 2026."
          //
          // It rendered inside C1 until the owner became choosable, and that was
          // fine while the owner was fixed. It is not a fact about the app: it
          // comes from `preview.existing`, the prior grant BETWEEN this app and
          // this owner. Switch to a group and the sentence means a different
          // grant, or none. So it follows C2 for the reason everything here
          // follows what it depends on — a date attributed to the wrong owner
          // is worse than no date, because it reads as a reassurance.
  'ADOPTION', // how many others connected this — it belongs to the DESCRIPTION of
              // the app, not to the terms, so it sits with the lines that name and
              // describe it. Between the price and the ask it separated two halves
              // of one decision with a fact about other people.
  'ROLE', // what a group lets you do there — the group → member half of a
          // membership, read only, ABOVE the half you choose (C4: what you share).
          // A group join is two contracts; the page states the one it does not
          // ask about before the one it does.
  'C3',   // the ask — editable, and BEFORE the realm
  'C5',   // retired: C3 is the actionable one. See note below.
  'C5P',  // retired: the realm IS the grant. See note below.
  'C6',   // item — the single node, or the diff
  'C4',   // realm — chooser  (AFTER C6: on a one-item act the item is
  //        the subject and the realm is where it lands, same as C4L)
  'C4L',  // realm — locked (derived / inherited / borrowed)
  //        AFTER the item, not before it. The only edges carrying both are the
  //        one-item shares, and there the locked realm is not "which part of
  //        you" — you answered that by choosing the item. It is where the item
  //        gets filed, which is a fact about the destination, not the offer.
  'FACE', // who you appear as — AFTER the realm, because the realm is what proposes
          // an identity. Stated above it, the page would name a face before the thing
          // that determines it has been chosen; pick two realms that disagree and the
          // question only exists once both are picked. It follows C4/C4L for the same
          // reason C4 follows the ask: a decision comes after what it depends on.
  'C7',   // boundary — everything you have, granted part lit
  'C8',   // tally — the count sentence
  'C9',   // risk — absent unless there is something to say
  'PRICE', // WHAT IT COSTS — at the END, with the other terms of the grant.
           //
           // It used to sit under C1, on the reasoning that a price gates whether
           // choosing between permissions matters at all. True, and it still read
           // wrong there: a figure two lines into the page, before the reader knows
           // what the app is asking for, is a number with nothing to weigh against.
           // Money is a TERM of the grant — like the expiry, like the realms — so it
           // belongs with the terms, where the decision is actually made: what it
           // takes, what it costs, how long it lasts, then Deny or Authorize.
           //
           // UNCONDITIONAL on the edges that carry it. It was conditional while a free
           // app rendered nothing, on the argument that a badge everywhere is noise —
           // but once some apps charge, an absent line cannot be told apart from a line
           // that failed to load, and that ambiguity is about somebody's money. A free
           // app now says so in the same slot, quietly.
  'C10',  // terms — now just the expiry control
  'RECEIPT', // what the grant hands over, itemised — read back before deciding
  'C11',  // decide
];

const ORDER = new Map(CANONICAL.map((c, i) => [c, i]));

/**
 * The matrix, as data. Keys are edge ids from the entity map.
 * `cond` marks a component present only when its data exists.
 */
/* C7 IS GONE FROM EVERY EDGE.
 *
 * "What it will see" listed the types the app asked for and the verbs the
 * chosen realms grant on each — line for line what the requirements list above
 * now shows, in a second visual grammar, one screen later. It was written when
 * the ask above was a static summary; once the realms began driving that list,
 * C7 became a restatement of it.
 *
 * IT USED TO STAY ON THE PUBLIC SHARE, on the reasoning that "there is no ask
 * and no Receipt, so it is the ONLY thing saying what becomes readable". The
 * second half of that had stopped being true: the Receipt was taught to work
 * without an ask when the friend and follow shares moved onto it (with none, it
 * is exactly what the realms reach), so the argument was circular — C7 stayed
 * because public had no RECEIPT, and public had no RECEIPT because it had C7.
 *
 * What it cost was a whole page in a grammar of its own. The public edge opened
 * its inventory UNFOLDED, forty-odd rows of every schema the realms reach, while
 * every other no-ask edge summarised the same thing on one folded line. The
 * widest share on the platform was the hardest one to read.
 *
 * So the public shares take the Receipt like everyone else, and C7 now names no
 * edge at all — it joins C5, C5P and C8 as a slot the sequence still knows and
 * no edge asks for. GrantRoute still SUPPLIES it, and that is deliberate rather
 * than leftover: a slot no sequence names is never rendered (see GrantPage), so
 * the component and its data stay one edit away from coming back without any
 * edge having quietly reacquired them in the meantime.
 *
 * WHAT WENT WITH IT on the ask edges: the folded "everything else you have,
 * none of it was asked for" list — the one place the page said what is NOT
 * shared. That is worth a sentence somewhere; it was not worth a section
 * behind a disclosure.
 */
/* C8 IS GONE FROM EVERY EDGE.
 *
 * "40 of 41 entries reachable" was a headline figure summarising what the rows
 * above already say — and it summarised toward a denominator (everything you
 * own) that the grant never offered. What it can DELETE is on the rows, verb by
 * verb; what it reaches is the realm's own business.
 */
/* RECEIPT — what the grant hands over, itemised, immediately before the
 * buttons. With an ask it totals what the ask and the chosen realms produce
 * together; with none (a group join, a share to a person) it is exactly what
 * the realms reach.
 */
/* PRICE IS ON THE APP EDGES ONLY — `u->a` and `g->a`.
 *
 * NOT on `u->s` / `g->s`, the sub-entity edges. An agent's exposure profile is capped by
 * its parent app's, so authorizing an agent grants nothing that was not already bought
 * when the app itself was authorized. A price on that screen would charge a second time
 * for the same access — and there is no second thing to buy.
 *
 * One purchase covers an app and every agent it declares. If that ever stops being true,
 * it is a product decision about sub-entities, not a missing slot here.
 */
export const EDGES = {
  /* A SHARE TO A PERSON IS A GROUP JOIN WITH ONE MEMBER, so it reads like one.
     Both state no ask, both hand over exactly what the chosen realms reach, and
     both ended on a different section: the join on the Receipt, the share on
     C7's inventory — a second grammar (a list of every type you have, a dashed
     "show 26 more" block) for the same fact, on pages a person meets minutes
     apart. The Receipt says it in the grammar every other edge now uses.
     A FRIEND SEES A FACE TOO, for the reason a group does: they read what you
     share, and the page should say who they are reading it from. The pick is
     sent with the share; `create_sharing` does not store it yet. */
  'u->u:friend':     { components: ['C1', 'WHY', 'C4', 'FACE', 'C10', 'RECEIPT', 'C11'] },
  'u->u:follow':     { components: ['C1', 'WHY', 'C4', 'C10', 'RECEIPT', 'C11'] },
  'u->u:node-share': { components: ['C1', 'WHY', 'C6', 'C4L', 'C10', 'C11'], act: true },
  /* ONE NOTE INTO A GROUP, and the same shape as the node share above it: C4L,
     not C4. The realm is not a choice here. Naming one of your realms would
     promise the group everything that realm covers, which is the trap the
     person-share avoids with its pair realm — so this edge files the note in the
     `-> <group>` realm on the contract membership already gave you, and the page
     reports that rather than asking. */
  'u->g:node-share': { components: ['C1', 'WHY', 'C6', 'C4L', 'C10', 'C11'], act: true },
  /* THE RECEIPT IS WHAT THIS EDGE NEEDED, and it was already listed — it simply
     never rendered, because the slot required an ask to itemise and a group join
     states none. So after choosing a realm the page went to the expiry: you had
     named a part of yourself and were never shown what was in it. The fix is in
     the slot (GrantRoute), not here: with no ask, what the grant hands over is
     exactly what the realms reach. */
  /* A GROUP SEES A FACE TOO. Joining one is a relationship like any other — the
     members read what you share, and something has to name who they are reading
     it from. Without this slot the group edge answered that question silently
     with your default, on the one screen whose job is to say what the other side
     gets.
     `All` does not appear here: it is offered to an app that ASKED to read your
     identities, and a group states no requirements at all. */
  'u->g:join':       { components: ['C1', 'WHY', 'ROLE', 'C3', 'C4', 'FACE', 'C10', 'RECEIPT', 'C11'],
                       cond: ['C3', 'ROLE'] },
  // C2 IS ON THE PERSONAL APP EDGE TOO, and conditional. It used to appear only
  // on `g->a`, because it only ever SAID something ("granting this as admin") and
  // there is nothing to say when the data is your own. It now also OFFERS the
  // choice, and the offer belongs on the edge where the choice is still open —
  // the one the app sent you to with no `group_id`. `cond` keeps it absent for
  // anyone who administers no groups, so that page is unchanged.
  'u->a':            { components: ['C1', 'WHY', 'C2', 'PRICE', 'ADOPTION', 'C3', 'C4', 'FACE', 'C9', 'C10', 'RECEIPT', 'C11'],
                       cond: ['C2', 'C9'] },
  /* A RUNTIME ASK WIDENS A GRANT THAT ALREADY EXISTS. The app asked mid-use —
     "read the schema you just picked as a signal source" — and the standing
     grant's terms were settled when it was made: its price was paid, its
     expiry set, its realms chosen. None of that is re-asked, and NO REALM IS
     CHOSEN: the ask is a row in the runtime-requests table, answered yes or
     no, and the platform bounds it on its own. What is on the table is the
     batch of items (C3, each with the app's reason), the terms it joins (C10,
     read back rather than set: which grant, and until when) and the receipt
     of exactly those rows. ADOPTION is a fact about first connecting, and this
     person already has. */
  // `noRealm`: the one edge that names no realm ON PURPOSE. Every other edge
  // must offer a chooser or a lock (grantOrder.realm.test.js); this one is not
  // a realm decision, and declaring that here is what keeps the invariant
  // strict for the rest.
  'u->a:runtime':    { components: ['C1', 'WHY', 'C3', 'C10', 'RECEIPT', 'C11'], noRealm: true },
  'u->s':            { components: ['C1', 'WHY', 'ADOPTION', 'C3', 'C4L', 'C10', 'RECEIPT', 'C11'] },
  'u->p':            { components: ['C1', 'WHY', 'C6', 'C4L', 'C10', 'C11'], act: true },
  'u->r':            { components: ['C1', 'WHY', 'C6', 'C10', 'C11'], sibling: true },
  // C4, NOT C4L. Publishing is a realm CHOICE — PUT /users/me/public-profile and
  // PUT /groups/{id}/public-profile both take privacy_realm_ids[], and the group
  // route refuses the request without one. Handing these edges the locked slot
  // rendered no chooser at all, on a page whose own footer said "choose at least
  // one realm above". There was no above.
  // FILING A NOTE INTO YOUR OWN REALM (ink's /share/realm). No counterparty:
  // the realm is a shelf, not a party. The note leads, the realm follows, and
  // there is no boundary section because nothing new becomes reachable — the
  // realm's EXISTING grantees gain one node.
  'u->realm':        { components: ['C1', 'WHY', 'C6', 'C4', 'C10', 'C11'], act: true },
  'u->pub':          { components: ['C1', 'WHY', 'C4', 'C9', 'C10', 'RECEIPT', 'C11'] },
  'g->a':            { components: ['C1', 'WHY', 'C2', 'PRICE', 'ADOPTION', 'C3', 'C4', 'FACE', 'C9', 'C10', 'RECEIPT', 'C11'],
                       cond: ['C9'] },
  // C2 says whose grant is widening; it never offers a choice here, because
  // the batch already names the EP it was addressed to.
  'g->a:runtime':    { components: ['C1', 'WHY', 'C2', 'C3', 'C10', 'RECEIPT', 'C11'], noRealm: true },
  'g->s':            { components: ['C1', 'WHY', 'C2', 'ADOPTION', 'C3', 'C4L', 'C10', 'RECEIPT', 'C11'] },
  'g->p':            { components: ['C1', 'WHY', 'C2', 'C6', 'C4L', 'C10', 'C11'], act: true },
  'g->r':            { components: ['C1', 'WHY', 'C2', 'C6', 'C10', 'C11'], sibling: true },
  'g->pub':          { components: ['C1', 'WHY', 'C2', 'C4', 'C9', 'C10', 'RECEIPT', 'C11'] },
};

/**
 * The components an edge renders, in canonical order.
 *
 * @param {string} edge  key into EDGES
 * @param {object} has   {C9: false, ...} — drop conditionals whose data is absent
 */
export function sequenceFor(edge, has = {}) {
  const cfg = EDGES[edge];
  if (!cfg) throw new Error(`grantOrder: unknown edge "${edge}"`);
  return cfg.components
    .filter((c) => (cfg.cond?.includes(c) ? has[c] !== false : true))
    .slice()
    .sort((a, b) => ORDER.get(a) - ORDER.get(b));
}

/** True when the edge is a one-shot act rather than a standing grant. */
export const isAct = (edge) => !!EDGES[edge]?.act;

/** True when the edge belongs on its own page built from the same library. */
export const isSibling = (edge) => !!EDGES[edge]?.sibling;


/**
 * Which edge a preview response describes.
 *
 * Derived from the SERVER's counterparty kind, not from which query param the
 * page happened to be handed — the same reason `acting_as` is server data. A
 * page that decides its own identity from the URL can be told to be a different
 * page by whoever wrote the link.
 *
 * Throws on an unrecognised combination rather than falling back to `u->a`,
 * because a wrong-but-plausible edge renders a confident page about the wrong
 * counterparty.
 */
export function edgeFor({ kind, actingAsGroup = false, nodeId = null, runtime = false } = {}) {
  const g = actingAsGroup;
  switch (kind) {
    // `runtime` is the server's `requester.runtime` block — the app is widening
    // a grant it already holds, not asking for one.
    case 'app':        return runtime ? (g ? 'g->a:runtime' : 'u->a:runtime')
                                      : (g ? 'g->a' : 'u->a');
    case 'sub_entity': return g ? 'g->s' : 'u->s';
    case 'plugin':     return g ? 'g->p' : 'u->p';
    case 'user':       return nodeId ? 'u->u:node-share' : 'u->u:friend';
    case 'follow':     return 'u->u:follow';
    case 'group':      return 'u->g:join';
    // Joining a group and handing it one note are different acts with the same
    // counterparty, which is why the server spells them with different kinds.
    case 'group_share': return 'u->g:node-share';
    case 'realm':      return 'u->realm';
    case 'public':     return g ? 'g->pub' : 'u->pub';
    case 'proposal':   return g ? 'g->r' : 'u->r';
    default:
      throw new Error(`grantOrder: no edge for counterparty kind "${kind}"`);
  }
}
